// Visible text of the host page – the values, error messages, and banners a
// user reports. The outline in `page-outline.ts` lists control labels only,
// so a KPI value or an error text inside a plain `div` never reaches the
// chatbot without this. The agent does not put this text in its prompts: it
// picks the few lines that match the user's message and quotes those.
//
// One line per small block, so a label stays with its value
// ("Average yield 93.1%"). Alerts come first: when the budget cuts a long
// page, the error the user is reporting is the last thing to drop.
//
// Form values are never read – only what the page itself renders. Elements
// the host excluded from session replay (`weloop-mask`, `weloop-block`, as a
// class or a `data-*` attribute) are excluded here too.

import { HOST_EXCLUDED_SELECTOR } from "@weloop/core/feedback/replay-masking";

const MAX_GROUP_LENGTH = 160;
const MAX_LINE_LENGTH = 200;
const MAX_LINES = 150;
const MAX_TEXT_LENGTH = 6000;
// `textContent` counts markup whitespace and hidden text, so it only works
// as a cheap upper bound before the exact visible length is computed.
const GROUP_PRECHECK_LENGTH = 600;

const ALERT_SELECTOR =
	'[role="alert"], [role="status"], [aria-live="assertive"], [aria-live="polite"], [aria-invalid="true"]';
// A block holding rows, list items, headings, or page regions is never
// merged into one line – each of those is a line of its own, so an error
// message is not glued to the navigation menu.
const STRUCTURE_SELECTOR =
	"tr, li, h1, h2, h3, h4, [role='row'], nav, main, header, footer, aside";
const EXCLUDED_SELECTOR = `${HOST_EXCLUDED_SELECTOR}, [contenteditable=""], [contenteditable="true"], [id^="weloop"]`;

// Tags whose text is never page content a user reads.
const SKIPPED_TAGS =
	/^(AUDIO|CANVAS|HEAD|IFRAME|INPUT|NOSCRIPT|OPTION|SCRIPT|SELECT|STYLE|TEMPLATE|TEXTAREA|VIDEO)$/u;
// Phrasing tags: text inside them stays on the line of the block around them.
const INLINE_TAGS =
	/^(A|ABBR|B|BDI|BR|CITE|CODE|EM|I|KBD|LABEL|MARK|Q|S|SMALL|SPAN|STRONG|SUB|SUP|TIME|U)$/u;

/**
 * Whether the host excluded an element from page context, on the element or
 * on one of its ancestors: replay-excluded, editable, Weloop's own, or
 * hidden. The outline checks one element at a time instead of walking the
 * tree, so it needs the ancestor check this walk gets for free.
 */
// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Element is an external mutable DOM interface
export function isExcludedFromPageContext(element: Element): boolean {
	return (
		element.closest(`${EXCLUDED_SELECTOR}, [hidden], [aria-hidden="true"]`) !==
		null
	);
}

const clean = (raw: string): string => raw.replaceAll(/\s+/gu, " ").trim();

// Alerts are emitted first, then skipped in the page walk so their text is
// not repeated inside the line of the block that contains them.
const emittedAlerts = new WeakSet<Element>();

// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Element and Node are external mutable DOM interfaces
function isReadable(element: Element): element is HTMLElement {
	if (!(element instanceof HTMLElement) || SKIPPED_TAGS.test(element.tagName)) {
		return false;
	}
	if (emittedAlerts.has(element)) {
		return false;
	}
	if (
		element.hidden === true ||
		element.getAttribute("aria-hidden") === "true" ||
		element.matches(EXCLUDED_SELECTOR)
	) {
		return false;
	}
	const style = getComputedStyle(element);
	return style.display !== "none" && style.visibility !== "hidden";
}

// The text a user can read inside `node`, words separated by one space.
// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Element and Node are external mutable DOM interfaces
function visibleText(node: Node): string {
	if (node.nodeType === Node.TEXT_NODE) {
		return node.textContent ?? "";
	}
	if (!(node instanceof Element) || !isReadable(node)) {
		return "";
	}
	const parts: string[] = [];
	for (const child of node.childNodes) {
		parts.push(visibleText(child));
	}
	return parts.join(" ");
}

// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Element and Node are external mutable DOM interfaces
const isInline = (node: Node): boolean =>
	node.nodeType === Node.TEXT_NODE ||
	(node instanceof Element && INLINE_TAGS.test(node.tagName));

/**
 * Collect the page's visible text, one line per small block, alerts first.
 * Returns an empty string when the page shows no readable text so callers
 * can omit the field.
 */
export function collectPageText(): string {
	const seen = new Set<string>();
	const lines: string[] = [];
	let length = 0;

	const isFull = (): boolean =>
		lines.length >= MAX_LINES || length >= MAX_TEXT_LENGTH;

	const emit = (raw: string): void => {
		const line = clean(raw).slice(0, MAX_LINE_LENGTH);
		// A line without a letter or digit is decoration (a separator, an icon).
		if (isFull() || seen.has(line) || !/[\p{L}\p{N}]/u.test(line)) {
			return;
		}
		seen.add(line);
		lines.push(line);
		length += line.length + 1;
	};

	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- Element and Node are external mutable DOM interfaces
	const walk = (element: Element): void => {
		if (isFull() || !isReadable(element)) {
			return;
		}
		const children = [...element.childNodes];
		if (children.every(isInline)) {
			emit(visibleText(element));
			return;
		}
		if (element.tagName === "TR") {
			emit([...element.children].map(visibleText).map(clean).join(" | "));
			return;
		}
		if (
			element.querySelector(STRUCTURE_SELECTOR) === null &&
			(element.textContent ?? "").length <= GROUP_PRECHECK_LENGTH
		) {
			const text = clean(visibleText(element));
			if (text.length <= MAX_GROUP_LENGTH) {
				emit(text);
				return;
			}
		}
		// Consecutive inline children form one line ("Total: <b>5</b>"); a
		// block child ends the run and is walked on its own.
		let run: string[] = [];
		const flush = (): void => {
			emit(run.join(" "));
			run = [];
		};
		for (const child of children) {
			if (isInline(child)) {
				run.push(visibleText(child));
			} else {
				flush();
				if (child instanceof Element) {
					walk(child);
				}
			}
		}
		flush();
	};

	const alerts = [...document.querySelectorAll(ALERT_SELECTOR)];
	for (const alert of alerts) {
		emittedAlerts.delete(alert);
	}
	for (const alert of alerts) {
		// An alert nested in an already emitted one is skipped by the mark.
		walk(alert);
		emittedAlerts.add(alert);
	}
	walk(document.body);
	return lines.join("\n");
}
